export class ApiError extends Error {
    readonly status?: number;
    readonly response?: Response;
    readonly body: unknown;
    readonly errorType?: string;
    readonly errorMessage?: string;
    readonly title?: string;
    readonly redirectTo?: string;
    readonly kind: 'network' | 'http' | 'parse';

    constructor(
        message: string,
        kind: 'network' | 'http' | 'parse',
        opts: {
            status?: number;
            response?: Response;
            body?: unknown;
            errorType?: string;
            errorMessage?: string;
            title?: string;
            redirectTo?: string;
        } = {}
    ) {
        super(message);
        this.name = 'ApiError';
        this.kind = kind;
        this.status = opts.status;
        this.response = opts.response;
        this.body = opts.body;
        this.errorType = opts.errorType;
        this.errorMessage = opts.errorMessage;
        this.title = opts.title;
        this.redirectTo = opts.redirectTo;
    }
}

interface BusinessErrorBody {
    Error: string;
    ErrorMessage?: string;
    Title?: string;
    RedirectTo?: string;
}

function isBusinessErrorBody(body: unknown): body is BusinessErrorBody {
    return typeof body === 'object' && body !== null && 'Error' in body && Boolean((body as { Error?: unknown }).Error);
}

function businessApiError(response: Response, body: BusinessErrorBody): ApiError {
    return new ApiError(body.ErrorMessage ?? body.Error, 'http', {
        status: response.status,
        response,
        body,
        errorType: body.Error,
        errorMessage: body.ErrorMessage,
        title: body.Title,
        redirectTo: body.RedirectTo,
    });
}

async function parseBody(response: Response): Promise<unknown> {
    let text: string;
    try {
        text = await response.text();
    } catch {
        throw new ApiError('Network request failed while reading response', 'network');
    }
    if (!text) return null;
    try {
        return JSON.parse(text) as unknown;
    } catch {
        throw new ApiError('Response body was not valid JSON', 'parse', { status: response.status, response });
    }
}

function withDefaults(init?: RequestInit): RequestInit {
    return {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        ...init,
    };
}

export const isNetworkError = (error: unknown): boolean => error instanceof ApiError && error.kind === 'network';

export async function hasSessionExpired(): Promise<boolean> {
    try {
        const response = await fetch('/api/user/getinfo', { credentials: 'include', redirect: 'manual' });
        return response.type === 'opaqueredirect' || response.status === 401;
    } catch {
        return false;
    }
}

export async function apiFetch<T = unknown>(url: string, init?: RequestInit): Promise<T | null> {
    return request<T>(encodeURI(url), init);
}

export function apiFetchNoEncode<T = unknown>(url: string, init?: RequestInit): Promise<T | null> {
    return request<T>(url, init);
}

async function request<T>(url: string, init?: RequestInit): Promise<T | null> {
    let response: Response;
    try {
        response = await fetch(url, withDefaults(init));
    } catch {
        throw new ApiError('Network request failed', 'network');
    }

    if (!response.ok) {
        // The exception middleware returns the error envelope for 4xx/5xx too, not only 200.
        const errorBody = await parseBody(response).catch(() => null);
        if (isBusinessErrorBody(errorBody)) {
            throw businessApiError(response, errorBody);
        }
        throw new ApiError(response.statusText || `Request failed with status ${response.status}`, 'http', {
            status: response.status,
            response,
            body: errorBody,
        });
    }

    if (response.status === 204) return null;

    const body = await parseBody(response);
    if (body === null) return null;

    if (isBusinessErrorBody(body)) {
        throw businessApiError(response, body);
    }

    return body as T;
}
